import { lazy, Suspense, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

import { CompilationDetailPreview } from "../../compilations/components/CompilationDetailPreview";
import { useSwipeDrag } from "../../hooks/useSwipeDrag";
import { About } from "../../routes/about";
import { Compilations } from "../../routes/compilations";
import { Gallery } from "../../routes/gallery";
import { Home } from "../../routes/home";
import { Copyright } from "../Copyright";
import { StaticBackground } from "../effects/StaticBackground";
import { StandardHeader } from "../header/StandardHeader";
import { getActiveNavIndex, NAV_LINKS } from "../sidebar/navOrder";

import { AnimatedOutlet } from "./transition/AnimatedOutlet";
import { DragCarousel } from "./transition/DragCarousel";
import { pageDragX } from "./transition/dragOffset";

// Lazy load shader effects to reduce initial bundle
const ShaderBackground = lazy(() =>
  import("../effects/ShaderBackground").then((m) => ({
    default: m.ShaderBackground,
  })),
);

/**
 * Resolve a stored nav path to its preview component for the drag carousel.
 * Strips query params so paths like /compilations/123?sort=date still match.
 */
function getPreviewForPath(fullPath: string): React.ReactNode | undefined {
  const pathname = fullPath.split("?")[0];
  if (pathname === "/") return <Home />;
  if (pathname === "/gallery") return <Gallery />;
  if (pathname === "/about") return <About />;
  if (pathname === "/compilations") return <Compilations />;
  if (pathname.startsWith("/compilations/")) {
    const compilationId = pathname.split("/")[2];
    if (compilationId)
      return <CompilationDetailPreview compilationId={compilationId} />;
    return <Compilations />;
  }
  return undefined;
}

/**
 * Remembers the last-visited path for each nav index so swiping back
 * restores non-canonical routes (e.g. /compilations/123 instead of /gallery).
 * Module-level so it survives re-renders within a single mount.
 * Cleared on fresh mounts (e.g. returning from ContentDetail) so stale
 * overrides don't carry over from a previous StandardLayout session.
 */
const lastPathByIndex = new Map<number, string>();

export function StandardLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  // Clear overrides on fresh mount. StandardLayout remounts when the
  // transition key changes (e.g. returning from /content/* to "main"),
  // so overrides only persist while navigating within StandardLayout.
  const isFreshMount = useRef(true);
  if (isFreshMount.current) {
    isFreshMount.current = false;
    lastPathByIndex.clear();
  }

  const currentIndex = getActiveNavIndex(location.pathname);

  // Record current full path (with search params) for this nav index.
  // Exclude /content paths — they live outside StandardLayout but still
  // map to the gallery nav index. During exit animations useLocation()
  // already reflects the new path, so without this guard a content path
  // would leak into lastPathByIndex and become the swipe-nav target.
  if (currentIndex >= 0 && !location.pathname.startsWith("/content/")) {
    lastPathByIndex.set(currentIndex, location.pathname + location.search);
  }

  const prevIndex = currentIndex > 0 ? currentIndex - 1 : null;
  const nextIndex =
    currentIndex > -1 && currentIndex < NAV_LINKS.length - 1
      ? currentIndex + 1
      : null;

  // Prefer last-visited path, fall back to canonical nav path
  const prevPath =
    prevIndex != null
      ? (lastPathByIndex.get(prevIndex) ?? NAV_LINKS[prevIndex].to)
      : null;
  const nextPath =
    nextIndex != null
      ? (lastPathByIndex.get(nextIndex) ?? NAV_LINKS[nextIndex].to)
      : null;

  const swipeHandlers = useSwipeDrag({
    resolve: (direction) => {
      if (direction === "left" && nextPath) return { type: "navigate" };
      if (direction === "right" && prevPath) return { type: "navigate" };
      // At boundary of canonical page — rubber-band
      return { type: "rubberband" };
    },
    onCommit: (_action, direction) => {
      if (direction === "left" && nextPath) {
        navigate(nextPath);
      } else if (direction === "right" && prevPath) {
        navigate(prevPath);
      }
    },
  });

  return (
    <div className="relative flex min-h-dvh flex-col lg:flex-row lg:justify-center">
      <Suspense fallback={<StaticBackground />}>
        <ShaderBackground />
      </Suspense>
      <StandardHeader className="relative z-20 pt-8 lg:w-1/7 lg:px-4 lg:pt-12" />
      <main
        className="relative z-10 min-w-0 lg:w-5/7 lg:px-4 lg:py-12"
        style={{ touchAction: "pan-y" }}
        {...swipeHandlers}
      >
        <DragCarousel
          dragValue={pageDragX}
          activeType="page"
          prev={prevPath ? getPreviewForPath(prevPath) : undefined}
          next={nextPath ? getPreviewForPath(nextPath) : undefined}
        >
          <AnimatedOutlet animateInitial={false} />
        </DragCarousel>
      </main>
      <Copyright className="relative z-10 mx-auto mt-auto mb-4 text-center text-xs text-neutral-600 lg:hidden" />
    </div>
  );
}
